import { useState, useCallback } from "react";
import { DropZone, Button } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setModalxComponent, setDestroy } from "components/modalx/modalx.slice";
import { notToSaveUpload } from "services/upload-file.service";
import { AppDispatch } from "configureStore";

type UploadFileProps = {
  label: string;
  /** Called with the response body once a file has been processed (shape not guaranteed). */
  onUploaded?: (data: unknown) => void;
  /** Called with a readable reason when the upload request fails. */
  onUploadError?: (message: string) => void;
};

/** The backend answers errors as `{ message }`, `{ error }` or a bare string. */
const uploadErrorMessage = (error: any): string => {
  const data = error?.response?.data;
  if (typeof data === "string" && data) return data;
  return data?.message || data?.error || error?.message || "";
};

const UploadFile: React.FC<UploadFileProps> = ({
  label,
  onUploaded,
  onUploadError,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { companyId } = useParams();
  const [openFileDialog, setOpenFileDialog] = useState(false);
  const { t } = useTranslation();

  const handleDropZoneDrop = useCallback(
    async (acceptedFiles: File[]) => {
      try {
        for (const file of acceptedFiles) {
          dispatch(setModalxComponent("Loading"));
          const { data } = await notToSaveUpload(file, companyId!);
          onUploaded?.(data);
        }
      } catch (error) {
        console.error(error);
        onUploadError?.(uploadErrorMessage(error));
      } finally {
        dispatch(setDestroy());
      }
    },
    [companyId, dispatch, onUploaded, onUploadError]
  );

  const toggleOpenFileDialog = useCallback(() => {
    setOpenFileDialog((prev) => !prev);
  }, []);

  return (
    <>
      <Button variant="primary" onClick={toggleOpenFileDialog}>
        {label}
      </Button>
      <div style={{ display: "none" }}>
        <DropZone
          allowMultiple={false}
          openFileDialog={openFileDialog}
          onDropAccepted={handleDropZoneDrop}
          onFileDialogClose={toggleOpenFileDialog}
        >
          <DropZone.FileUpload />
        </DropZone>
      </div>
    </>
  );
};

export default UploadFile;
